// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { PLANTING_STATUS } from '@seedplanner/shared-utils';
import type Decimal from 'decimal.js';
import * as React from 'react';
import { Table } from '../common/table';
import type { ColumnType } from '../common/table/Table';

export interface TrayChangeInfo {
  readonly cropId: string;
  readonly cropName: string;
  readonly totalGrowTime: number;
  readonly harvestBufferTime: number;
  readonly cropCanBeStartedInTime: boolean;
  readonly didChange: boolean;
  readonly isAdded: boolean;
  readonly isRemoved: boolean;
  readonly existingTrays: Decimal;
  readonly newTrays: Decimal;
  readonly trayDiff: Decimal;
  readonly surplusTraysAvailable: Decimal;
  readonly plantingData?: PlantingData;
}

interface PlantingData {
  readonly planting_status: string;
}

interface Props {
  readonly changes: TrayChangeInfo[];
}

export function getChangeAction(change: TrayChangeInfo): string {
  if (!change.didChange || change.trayDiff.isZero()) {
    return 'No Change';
  }
  if (change.isRemoved) {
    if (change.plantingData?.planting_status === PLANTING_STATUS.IN_PROGRESS) {
      return `Move ${change.trayDiff.abs().toFixed(2)} Trays To Surplus`;
    }
    if (change.plantingData?.planting_status === PLANTING_STATUS.COMPLETED) {
      return 'Planting Already Completed';
    }
    return 'Delete Planting';
  }

  // Trying to increase trays
  if (change.trayDiff.isPositive()) {
    // If there is a planting and it's in progress, try pulling from the surplus
    if (change.plantingData?.planting_status === PLANTING_STATUS.IN_PROGRESS) {
      // This may change to allow the user to create a new planting even if the crop is too late to start
      // Get the amout available from surplus to pull
      const hasEnoughSurplus = change.surplusTraysAvailable.gte(change.trayDiff);
      if (hasEnoughSurplus) {
        return `Pull ${change.trayDiff.toFixed(2)} Trays From Surplus`;
      }
      const hasAnySurplus = change.surplusTraysAvailable.gt(0);
      if (hasAnySurplus) {
        const deficitAfterSurplus = change.trayDiff.sub(change.surplusTraysAvailable);
        if (change.cropCanBeStartedInTime) {
          return `Pull ${change.surplusTraysAvailable.toFixed(2)} Trays From Surplus. Create ${deficitAfterSurplus.toFixed(2)} Tray Planting`;
        }

        return `Pull ${change.surplusTraysAvailable.toFixed(2)} Trays From Surplus. ${deficitAfterSurplus.toFixed(2)} Deficit`;
      }

      // If it can be started in time, create a new planting
      if (change.cropCanBeStartedInTime) {
        return `Create ${change.trayDiff.toFixed(2)} Tray Planting`;
      }

      return `No Surplus Available. ${change.trayDiff.toFixed(2)} Deficit`;
    }
    if (change.plantingData?.planting_status === PLANTING_STATUS.COMPLETED) {
      return 'Planting Completed';
    }

    if (change.isAdded) {
      const hasEnoughSurplus = change.surplusTraysAvailable.gte(change.trayDiff);
      if (hasEnoughSurplus) {
        return `Pull ${change.trayDiff.toFixed(2)} Trays From Surplus`;
      }
      const hasAnySurplus = change.surplusTraysAvailable.gt(0);
      if (hasAnySurplus) {
        const deficitAfterSurplus = change.trayDiff.sub(change.surplusTraysAvailable);
        if (change.cropCanBeStartedInTime) {
          return `Pull ${change.surplusTraysAvailable.toFixed(2)} Trays From Surplus. Create ${deficitAfterSurplus.toFixed(2)} Tray Planting`;
        }

        return `Pull ${change.surplusTraysAvailable.toFixed(2)} Trays From Surplus. ${deficitAfterSurplus.toFixed(2)} Deficit`;
      }

      // If it can be started in time, create a new planting
      if (change.cropCanBeStartedInTime) {
        return `Create ${change.trayDiff.toFixed(2)} Tray Planting`;
      }

      return `No Surplus Available. ${change.trayDiff.toFixed(2)} Deficit`;
    }

    return 'Update Planting';
  }

  // Trying to decrease trays (and not remove the crop)
  if (change.plantingData?.planting_status === PLANTING_STATUS.IN_PROGRESS) {
    return `Move ${change.trayDiff.abs().toFixed(2)} Trays To Surplus`;
  }
  if (change.plantingData?.planting_status === PLANTING_STATUS.COMPLETED) {
    return 'Planting Completed';
  }
  // Not possible to decrease the trays without an existing planting
  if (change.plantingData === undefined) {
    return '-';
  }

  return 'Update Planting';
}

export default function OrderInputsChangeTable({ changes }: Props) {
  const columns: ColumnType[] = [
    {
      name: 'Crop',
      values: changes.map((change) => change.cropName),
    },
    {
      name: 'Old Trays',
      smallName: 'Old',
      values: changes.map((change) => change.existingTrays.toFixed(2)),
      numeric: true,
      visibleAt: 'sm',
    },
    {
      name: 'New Trays',
      smallName: 'New',
      values: changes.map((change) => change.newTrays.toFixed(2)),
      numeric: true,
      visibleAt: 'sm',
    },
    {
      name: 'Tray Change',
      smallName: 'Change',
      values: changes.map((change) => (change.didChange ? change.trayDiff.toFixed(2) : '-')),
      numeric: true,
    },
    {
      name: 'Action',
      values: changes.map((change) => {
        return getChangeAction(change);
      }),
    },
  ];

  return <Table columns={columns} />;
}
